import {supabase} from './BackendService'

export type User = {
  id: string
  name: string
}

export type UserSettings = {
  userName: string
  metricSystem: MetricSystem
  email?: string
}

export const MetricSystemMetadata = {metric: {label: 'metric'}, imperial: {label: 'imperial'}}

export type MetricSystem = keyof typeof MetricSystemMetadata

export class UserService {
  public async getCurrentUser(): Promise<User | null> {
    const {data, error} = await supabase.auth.getSession()
    if (error || !data.session) {
      return null
    }
    return {id: data.session.user.id, name: data.session.user.email}
  }

  public async signInGoogle() {
    const {data, error} = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: process.env.AUTH_REDIRECT,
      },
    })
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async signOut() {
    localStorage.clear()
    const {error} = await supabase.auth.signOut()
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async signUpEmailPassword(email: string, password: string) {
    const {data, error} = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
      },
    })
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async signInEmailPassword(email: string, password: string) {
    const {data, error} = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async resetPassword(email: string) {
    const {data, error} = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/change-password`,
    })

    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async changePassword(password: string) {
    const {data, error} = await supabase.auth.updateUser({password})
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async signUpGoogle() {
    const {data, error} = await supabase.auth.signInWithOAuth({
      provider: 'google',
    })
    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public async getUserSettings(): Promise<
    | {success: false; error: string}
    | {
        success: true
        data: UserSettings
      }
  > {
    //проверить тип возвращаемого значения
    const {data, error} = await supabase
      .from('user_settings')
      .select('id, userName, metricSystem')
      .eq('id', (await supabase.auth.getSession()).data.session.user.id)

    if (error) {
      return {success: false, error: error.message}
    } else if (!data || data.length === 0) {
      return {
        success: true,
        data: {
          userName: '',
          metricSystem: 'metric',
          email: (await supabase.auth.getSession()).data.session.user.email,
        } as UserSettings,
      }
    } else {
      return {success: true, data: data[0] as any}
    }
  }

  public async changeUserSettings(userSettings: UserSettings) {
    const {data, error} = await supabase.from('user_settings').upsert({
      metricSystem: userSettings.metricSystem,
      userName: userSettings.userName,
      id: (await supabase.auth.getSession()).data.session.user.id,
    })
    if (error) {
      return {success: false, error: error.message}
    } else {
      return {success: true}
    }
  }

  public async deleteCurrentUser() {
    const userId = (await supabase.auth.getSession()).data.session.user.id
    const {data, error} = await supabase.functions.invoke(`delete-user`, {
      body: {
        userId: userId,
      },
    })

    if (error) {
      return {success: false, error}
    }
    this.signOut()
    return {success: true, error}
  }

  // TODO: move locales to a separate type
  public getSavedUserLocale(): 'ru' | 'en' | undefined {
    const userDefinedSavedLocale = localStorage.getItem('locale')
    if (userDefinedSavedLocale !== 'ru' && userDefinedSavedLocale !== 'en') {
      return undefined
    }

    return userDefinedSavedLocale
  }
}

export const instance = new UserService()
